<script setup lang="ts">
import { type TimelineItem } from '~/models/arbitrary';

type Props = {
  items: TimelineItem[];
};

const props = defineProps<Props>();

const isImageless = props.items.every((item) => !item.image);
const isRightSided = props.items.every((item) => item.position === 'right');
const isLeftSided = props.items.every((item) => item.position === 'left');
</script>

<template>
  <div
    class="timeline"
    :class="{
      'timeline__item--right-side': isRightSided,
      'timeline__item--left-side': isLeftSided,
      'timeline__item--imagesless': isImageless,
    }"
  >
    <div
      v-for="(item, index) in items"
      :key="item.id"
      class="timeline__item"
      :class="{
        [`timeline__item--${item.position}`]: true,
        'timeline__item--first': index === 0,
        'timeline__item--last': index === items.length - 1,
      }"
    >
      <div v-if="!isImageless" class="timeline__image">
        <img alt="" aria-hidden="true" loading="lazy" :src="item.image" :srcset="item.imageSet" />
      </div>
      <div class="timeline__number">
        <span>{{ index + 1 }}</span>
      </div>
      <div class="timeline__content">
        <div class="timeline__card card card--dark">
          <h5>{{ item.title }}</h5>
          <span>{{ item.content }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.timeline {
  --row-gap: 2.5rem;

  display: flex;
  flex-direction: column;
  gap: 1.875rem;

  &.timeline__item--right-side.timeline__item--imagesless,
  &.timeline__item--left-side.timeline__item--imagesless {
    .timeline__item {
      &.timeline__item--right {
        grid-template-columns: 3.125rem 1fr;
        grid-template-areas: 'number content';
      }

      &.timeline__item--left {
        grid-template-columns: 1fr 3.125rem;
        grid-template-areas: 'content number';
      }
    }
  }

  &.timeline__item--right-side {
    @media (max-width: 787.98px) {
      .timeline__item {
        grid-template-columns: 3.125rem 1fr;
        grid-template-areas:
          'number image'
          'number content';
      }
    }
  }

  &.timeline__item--left-side {
    @media (max-width: 787.98px) {
      .timeline__item {
        grid-template-columns: 1fr 3.125rem;
        grid-template-areas:
          'image number'
          'content number';
      }
    }
  }

  .timeline__item {
    display: grid;
    grid-template-columns: 3.125rem 1fr;
    grid-template-areas: 'number content';
    gap: var(--row-gap);
    align-items: center;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 3.125rem 1fr;
      grid-template-areas: 'image number content';
    }

    &.timeline__item--left {
      grid-template-areas: 'content number';

      @media (min-width: 768px) {
        grid-template-areas: 'content number image';
      }
    }

    &.timeline__item--first {
      .timeline__number {
        &::after {
          top: 50%;
        }
      }
    }

    &.timeline__item--last {
      .timeline__number {
        &::after {
          bottom: 50%;
        }
      }
    }
  }

  .timeline__image {
    display: none;

    grid-area: image;

    @media (min-width: 768px) {
      display: block;
    }

    img {
      margin: 0 auto;
    }
  }

  .timeline__number {
    grid-area: number;

    position: relative;
    z-index: 1;

    display: flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;

    span {
      z-index: 2;

      height: 4.375rem;
      width: 100%;

      font-weight: 700;
      font-size: var(--font-size-xxxl);
      line-height: 4.375rem;
      text-align: center;

      background-color: var(--brown-darkened);
    }

    &::after {
      content: ' ';

      position: absolute;
      z-index: 0;
      bottom: calc(var(--row-gap) * -0.5);
      top: calc(var(--row-gap) * -0.5);

      width: 0.0625rem;

      background-color: var(--gray-light);
    }
  }

  .timeline__content {
    grid-area: content;
  }
}
</style>
